import { api } from '@/lib/axios';
import type { ExportRequest } from '@/lib/export-request';
import type {
  Project,
  PaginatedProjects,
  ListProjectsParams,
  ProjectListFigures,
  ProjectPosition,
  ProjectWorkStep,
} from '../types/project.types';

// ── Projektliste (paginiert + gefiltert) ─────────────────────────────────────

export async function fetchProjects(
  params: ListProjectsParams,
): Promise<PaginatedProjects> {
  const res = await api.get<PaginatedProjects>('/projects', { params });
  return res.data;
}

/** AP-35 Nachtrag 5 — Nachbarn im Detailkopf, in der Reihenfolge der gefilterten Liste. */
export interface ProjectNeighbors {
  previous: { id: string; projectNumber: string; name: string } | null
  next: { id: string; projectNumber: string; name: string } | null
  /** 1-basiert; `null`, wenn das Projekt nicht in der gefilterten Liste steht */
  position: number | null
  total: number
}

export async function fetchProjectNeighbors(
  id: string,
  params: Omit<ListProjectsParams, 'page' | 'limit'>,
): Promise<ProjectNeighbors> {
  const res = await api.get<{ data: ProjectNeighbors }>(`/projects/${id}/neighbors`, { params });
  return res.data.data;
}

/**
 * Phase K — Kennzahlen für die sichtbaren Zeilen (nur wenn eine Kennzahl-Spalte
 * eingeblendet ist). `capabilities` sagt, welche Beträge der Server herausgibt.
 */
export async function fetchProjectListFigures(ids: string[]): Promise<{
  data: Record<string, ProjectListFigures>;
  capabilities: { verkauf: boolean; kosten: boolean };
}> {
  const res = await api.get('/projects/list-figures', { params: { ids: ids.join(',') } });
  return res.data;
}

/** Phase K — Export der Projektliste (Listen-Standard, Mailweg `EXPORT_PROJECTS`). */
export function projectsExportRequest(
  params: ListProjectsParams & { format: string; orientation?: string; filterInfo?: string },
  columns: { key: string; label: string }[],
): ExportRequest {
  return { url: '/projects/export', params: { ...params, page: undefined, limit: undefined, columns: JSON.stringify(columns) } };
}

// ── Projektdetail inkl. Positionen + ProjectWorkStepe ─────────────────────────

export async function fetchProject(id: string): Promise<Project> {
  const res = await api.get<{ data: Project }>(`/projects/${id}`);
  return res.data.data;
}

// ── Positionen eines Projekts ─────────────────────────────────────────────────

export async function fetchPositions(projectId: string): Promise<ProjectPosition[]> {
  const res = await api.get<{ data: ProjectPosition[] }>(`/projects/${projectId}/positions`);
  return res.data.data;
}

// ── ProjectWorkStepe einer Position ───────────────────────────────────────────

export async function fetchSteps(
  projectId: string,
  posId: string,
): Promise<ProjectWorkStep[]> {
  const res = await api.get<{ data: ProjectWorkStep[] }>(
    `/projects/${projectId}/positions/${posId}/steps`,
  );
  return res.data.data;
}

// ── Aktive Projekte für Dropdown ──────────────────────────────────────────────

export async function fetchActiveProjects(): Promise<
  Pick<Project, 'id' | 'projectNumber' | 'name' | 'externalId' | 'externalSource'>[]
> {
  const res = await api.get<{
    data: Pick<Project, 'id' | 'projectNumber' | 'name' | 'externalId' | 'externalSource'>[];
  }>('/projects/dropdown');
  return res.data.data;
}
